/* eslint-disable max-params */
/* eslint-disable complexity */
import { BlueprintContext } from '../../blueprint-context/blueprint-context.class';
import { Components } from '../../../component-classes';
import { getBottomRevealCfn } from './get-bottom-reveal.cfn';
import { PanelItem } from '../panel-item.class';
import { PanelItemGroup } from '../../panel-item-groups/panel-item-group.class';
import { Vertex3 } from '../../../spatial-classes';

/**
 * Applies BoundToEdge to panelItem origin
 *
 * @param panelItem instance of PanelItem
 * @param panelItemGroup instance of PanelItemGroup
 * @param center panelItem origin
 * @returns Point representing origin
 */
export const getItemCenterCfn = (panelItem: PanelItem, container: PanelItemGroup, center: Vertex3, context: BlueprintContext = null): Vertex3 => {
	// no container, then retrurn center as absolute position
	if (container === null) {
		console.log('panelItem:getCenterCfn (no container)', center);
		return center;
	}

	const halfContainerHeight = (container.height ?? 0) / 2;
	// const halfContainerWidth = (container.width ?? 0) / 2;
	const halfContainerDepth = (container.depth ?? 0) / 2;

	// handle x binding
	if (panelItem.boundLeft) {
		// left justified in container
		center.x = (panelItem.item.size.width - container.width) / -2;
	} else if (panelItem.boundRight) {
		// right justified in container
		center.x = (panelItem.item.size.width - container.width) / 2;
	} else {
		// use absolute position
		center.x = center.x ?? 0;
	}

	// handle y binding
	if (panelItem.fullHeight) {
		center.y = halfContainerHeight - (panelItem.size.height / 2);
	} else if (panelItem.boundTop) {
		const countertopThickness = panelItem.refMap?.island?.getCountertop()?.material.thickness ?? 0;

		if (panelItem.item.placement === Components.PLACEMENT_FRONT_ONLY) {
			// center.y = ((container.height ?? 0) / -2) + (panelItem.size.height / 2);
			center.y = -halfContainerHeight + (panelItem.size.height / 2);
		} else {
			// center.y = ((container.height ?? 0) / -2) + panelItem.item.distanceBelowCountertop - countertopThickness - (panelItem.size.height / 2);
			center.y = -halfContainerHeight + panelItem.item.distanceBelowCountertop - countertopThickness - (panelItem.size.height / 2);
		}
		if (panelItem.sleeve) {
			const component = context.components.findId(panelItem.sleeve);
			// Only apply the sleeve-depth offset for true sleeves (C1SLV / C3SLV).
			// Insulated jackets resolve as the sibling sleeve but have sleeveDepth=null,
			// so subtracting it would produce NaN and float the inner appliance.
			if (component && component.sleeveDepth !== null) center.y += component.sleeveDepth - panelItem.item.distanceBelowCountertop - countertopThickness;
		}
		center.y += panelItem.offset.y;
	} else if (panelItem.boundBottom) {
		// negative Y is up: the reveal lifts the item off the panel's bottom edge
		center.y = halfContainerHeight - (panelItem.size.height / 2) - getBottomRevealCfn(panelItem);
		center.y += panelItem.offset.y;
	}

	if (panelItem.boundTop && panelItem.item.placement === Components.PLACEMENT_FRONT_AND_TOP) {
		center.z = halfContainerDepth - ((panelItem.size.depth - (panelItem.item.distanceFrontOfFace ?? 0)) / 2);
		// center.z = halfContainerDepth + (panelItem.item.distanceFrontOfFace ?? 0) - (panelItem.size.depth / 2);
	// eslint-disable-next-line no-negated-condition
	} else if (panelItem.item.distanceFrontOfFace !== null || panelItem.item.placement === Components.PLACEMENT_FRONT_ONLY) {
		center.z = halfContainerDepth - ((panelItem.size.depth - (panelItem.item.distanceFrontOfFace ?? 0)) / 2);
		// center.z = halfContainerDepth + ((panelItem.item.distanceFrontOfFace ?? 0)) - (panelItem.size.depth / 2);
	} else if (center.z === 0 && panelItem.boundTop) {
		// distanceBackOfFace is back-edge-of-item to back-edge-of-panel; null keeps the 1/8" clearance default
		center.z = panelItem.item.distanceBackOfFace !== null
			? -halfContainerDepth + panelItem.item.distanceBackOfFace + (panelItem.size.depth / 2)
			: -halfContainerDepth - (panelItem.size.depth / 2) + 0.125;
	}

	// console.log('panelItem:getCenterCfn', center);
	return center as Vertex3;
};
